import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import styles from './FJImagePreviewModal.module.less';
import FJModalClose from '../FJModalClose/FJModalClose';
import FJImageItemPreview from '../../../common/components/FJImageItemPreview/FJImageItemPreview';
import PreviewGradientArrow from '../../../assets/svg/preview-gradient-arrow.svg?react';
import FJDeleteWhiteIcon from '../../../assets/svg/delete-white.svg?react';
import FJDownloadIcon from '../../../assets/svg/download.svg?react';

import classNames from 'classnames';
import FJToolTip from '../../../common/components/FJToolTip/FJToolTip';
import FJLocalStore from '../../../store/FJLocalStore';

const FJImagePreviewModal = ({
    active,
    onClose,
    imageList = [],
    currentIndex = 0,
    onDelete,
    onDownload,
    showDeleteButton,
    showDownloadButton,
}) => {
    const [currentIdx, setCurrentIdx] = useState(currentIndex);
    const [displayList, setDisplayList] = useState(imageList);

    // 同步外部传入的 currentIndex
    useEffect(() => {
        setCurrentIdx(currentIndex);
    }, [currentIndex]);

    // 当模态框显示时，重置到指定索引
    useEffect(() => {
        if (active) {
            setCurrentIdx(currentIndex);
            setDisplayList(imageList);
        }
    }, [active, currentIndex, imageList]);

    const currentImage = useMemo(() => displayList[currentIdx] || {}, [currentIdx, displayList]);

    const handlePrevious = useCallback(() => {
        if (currentIdx > 0) {
            const newIndex = currentIdx - 1;
            setCurrentIdx(newIndex);
        }
    }, [currentIdx]);

    const handleNext = useCallback(() => {
        if (currentIdx < imageList.length - 1) {
            const newIndex = currentIdx + 1;
            setCurrentIdx(newIndex);
        }
    }, [currentIdx, imageList.length]);

    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const handleDelete = useCallback(async () => {
        onDelete?.([currentImage.id], () => {
            // update display list and currentIdx to next one, if it is last use previous one, if it is empty, call close
            const newList = displayList.filter(item => item.id !== currentImage.id);
            setDisplayList(newList);
            if (newList.length === 0) {
                onClose?.();
            } else {
                setCurrentIdx(prev => prev % newList.length);
            }
        });
    }, [onDelete, currentImage.id, onClose, displayList]);

    const handleDownload = useCallback(() => {
        onDownload?.([currentImage.id]);
    }, [onDownload, currentImage.id]);

    const isPreviousDisabled = currentIdx <= 0;
    const isNextDisabled = currentIdx >= displayList.length - 1;

    if (!active || imageList.length === 0) {
        return null;
    }

    return (
        <div className={styles.box}>
            <FJModalClose onClick={handleClose} />
            {/* 导航按钮 */}
            {displayList.length > 1 && (
                <>
                    <button
                        className={classNames(styles.nav_button, styles.prev_button, {
                            [styles.disabled]: isPreviousDisabled,
                        })}
                        onClick={handlePrevious}
                        disabled={isPreviousDisabled}
                    >
                        <PreviewGradientArrow />
                    </button>

                    <button
                        className={classNames(styles.nav_button, styles.next_button, {
                            [styles.disabled]: isNextDisabled,
                        })}
                        onClick={handleNext}
                        disabled={isNextDisabled}
                    >
                        <PreviewGradientArrow />
                    </button>
                </>
            )}
            <div className={styles.content}>
                <div className={styles.preview_container}>
                    <FJImageItemPreview
                        data={currentImage}
                        showInfo={false}
                        imgUrl={currentImage.imgUrl}
                        originalUrl={currentImage.originalUrl}
                        isShowShareIcon={false}
                    />
                </div>

                {/* 图片计数器 */}
                {displayList.length > 1 && (
                    <div className={styles.counter}>
                        {currentIdx + 1} / {displayList.length}
                    </div>
                )}
                <div className={styles.image_actions}>
                    {showDeleteButton && (
                        <FJToolTip title={FJLocalStore._('delete')}>
                            <div
                                className={styles.delete_button}
                                onClick={e => {
                                    e.stopPropagation();
                                    handleDelete();
                                }}
                            >
                                <FJDeleteWhiteIcon />
                            </div>
                        </FJToolTip>
                    )}
                    {showDownloadButton && (
                        <FJToolTip title={FJLocalStore._('download')}>
                            <div
                                className={styles.download_button}
                                onClick={e => {
                                    e.stopPropagation();
                                    handleDownload();
                                }}
                            >
                                <FJDownloadIcon />
                            </div>
                        </FJToolTip>
                    )}
                </div>
            </div>
        </div>
    );
};

FJImagePreviewModal.propTypes = {
    active: PropTypes.bool,
    onClose: PropTypes.func,
    imageList: PropTypes.arrayOf(
        PropTypes.shape({
            imgUrl: PropTypes.string,
            originalUrl: PropTypes.string,
            width: PropTypes.number,
            height: PropTypes.number,
            id: PropTypes.number,
            // 其他图片数据字段
        }),
    ),
    currentIndex: PropTypes.number,
    onDelete: PropTypes.func,
    onDownload: PropTypes.func,
    showDeleteButton: PropTypes.bool,
    showDownloadButton: PropTypes.bool,
};

export default FJImagePreviewModal;
